import { useEffect, useState } from 'react';
import {
	ActionButton,
	BaseCard,
	EmptyState,
	ExportDropdown,
	FilterChip,
	ListPageLayout,
	Pagination,
	SectionCard,
	SideListCard,
	SkeletonBox,
	useToast,
} from '@practium/ui';
import type { SectionTab } from '@practium/ui';
import { DefensivesTable } from '../components/Tables/DefensivesTable/DefensivesTable';
import { FertilizersTable } from '../components/Tables/FertilizersTable/FertilizersTable';
import { ManufacturersTable } from '../components/Tables/ManufacturersTable/ManufacturersTable';
import type { UseProductsPageProps } from '../hooks/useProductsPage';
import { useProductsPage } from '../hooks/useProductsPage';
import { useImportResultToast } from '../hooks/useImportResultToast';
import { useImportDefensiveFile } from '../../api/hooks/defensives/useImportDefensiveFile';
import { useExportDefensives } from '../../api/hooks/defensives/useExportDefensives';
import { useExportFertilizers } from '../../api/hooks/fertilizers/useExportFertilizers';
import { useImportFertilizerFile } from '../../api/hooks/fertilizers/useImportFertilizerFile';
import { useImportManufacturerFile } from '../../api/hooks/manufacturers/useImportManufacturerFile';
import { useGetMostPopularManufacturers } from '../../api/hooks/manufacturers/useGetMostPopularManufacturers';
import { useGetMostRecommendedProduct } from '../../api/hooks/data-reports/useGetMostRecommendedProduct';
import type { ProductsTab } from '../types';
import {
	exportManufacturersToExcel,
	exportManufacturersToPdf,
} from '../utils/exportProducts';
import { ImportProductsModal } from '../components/ImportProductsModal/ImportProductsModal';
import { ProductPhotoCarousel } from '../components/ProductPhotoCarousel/ProductPhotoCarousel';
import { ProductModel } from '../components/3D/ProductModel';
import styles from './ProductsPage.module.css';
import { FactoryOutlined } from '@mui/icons-material';
import { ProductsPageSkeleton } from '../components/Skeletons/ProductsPageSkeleton';

const ICON_BG_CLASSES = [styles.iconBgA, styles.iconBgB, styles.iconBgC];

const ICON_CLASSES = [styles.iconA, styles.iconB, styles.iconC];

export interface ProductsPageProps extends UseProductsPageProps {
	onAddNew?: (tab: ProductsTab) => void;
	onViewItem?: (tab: ProductsTab, id: string) => void;
	addButtonText?: string;
	onAddButtonClick?: () => void;
	onImport?: (file: File, tab: ProductsTab) => void;
}

export const ProductsPage = ({
	onAddNew,
	onViewItem,
	addButtonText,
	onAddButtonClick,
	onImport,
	...hookProps
}: ProductsPageProps) => {
	const [isImportModalOpen, setIsImportModalOpen] = useState(false);
	const importDefensiveFile = useImportDefensiveFile();
	const importFertilizerFile = useImportFertilizerFile();
	const exportFertilizers = useExportFertilizers();
	const exportDefensives = useExportDefensives();
	const isExporting = exportFertilizers.isPending || exportDefensives.isPending;
	const toast = useToast();
	const handleExportProducts = async (
		tab: 'defensives' | 'fertilizers',
		format: 'pdf' | 'xlsx',
		filters: {
			filter?: string;
			sortAsc?: boolean;
			class?: string[];
			category?: string[];
			nature?: string[];
			origin?: string[];
			manufacturer?: string[];
		}
	) => {
		try {
			const mutation = tab === 'defensives' ? exportDefensives : exportFertilizers;
			const { file, filename } = await mutation.mutateAsync({ format, ...filters });
			const url = URL.createObjectURL(file);
			const link = document.createElement('a');
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			try {
				link.click();
			} finally {
				link.remove();
				window.setTimeout(() => URL.revokeObjectURL(url), 1000);
			}
		} catch {
			toast.error(`Erro ao exportar ${tab === 'defensives' ? 'defensivos' : 'fertilizantes'}. Tente novamente.`);
		}
	};
	const importManufacturerFile = useImportManufacturerFile();
	const { showImportResultToast } = useImportResultToast();
	const {
		data: mostPopularProduct,
		isPending: isMostPopularProductLoading,
		isFetching: isMostPopularProductFetching,
		isError: isMostPopularProductError,
	} = useGetMostRecommendedProduct();
	const {
		data: mostPopularManufacturers = [],
		isPending: isMostPopularManufacturersLoading,
		isFetching: isMostPopularManufacturersFetching,
		isError: isMostPopularManufacturersError,
	} = useGetMostPopularManufacturers();
	const {
		activeTab,
		setActiveTab,
		filteredDefensives,
		filteredFertilizers,
		filteredManufacturers,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		parsedFilters,
		remoteFilter,
		remoteSortAsc,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		resolveUserName,
		isTableLoading,
		isTableFetching,
		isTableError,
		refetchTable,
		isFiltersLoading,
		isFiltersError,
	} = useProductsPage(hookProps);
	const [hasRenderedContent, setHasRenderedContent] = useState(false);
	const isInitialLoading =
		!hasRenderedContent &&
		isTableLoading &&
		!isTableError &&
		isMostPopularProductLoading &&
		isMostPopularManufacturersLoading;

	useEffect(() => {
		if (!isInitialLoading) setHasRenderedContent(true);
	}, [isInitialLoading]);

	if (isInitialLoading) return <ProductsPageSkeleton />;

	const tableStatus = isTableError ? (
		<div role="alert" className={styles.error}>
			<EmptyState
				title="Não foi possível carregar o catálogo"
				description="Tente novamente para carregar os dados."
			/>
			<ActionButton text="Tentar novamente" onClick={() => void refetchTable()} />
		</div>
	) : null;

	const tabs: SectionTab<ProductsTab>[] = [
		{
			key: 'defensives',
			label: 'Defensivos',
			content: (
				<DefensivesTable
					defensives={filteredDefensives}
					resolveUserName={resolveUserName}
					onRowClick={onViewItem ? (id) => onViewItem('defensives', id) : undefined}
				/>
			),
		},
		{
			key: 'fertilizers',
			label: 'Fertilizantes',
			content: (
				<FertilizersTable
					fertilizers={filteredFertilizers}
					resolveUserName={resolveUserName}
					onRowClick={onViewItem ? (id) => onViewItem('fertilizers', id) : undefined}
				/>
			),
		},
		{
			key: 'manufacturers',
			label: 'Fabricantes',
			content: (
				<ManufacturersTable
					manufacturers={filteredManufacturers}
					onRowClick={onViewItem ? (id) => onViewItem('manufacturers', id) : undefined}
				/>
			),
		},
	];

	const sideListItems = mostPopularManufacturers.map((manufacturer, index) => ({
		id: manufacturer._id,
		label: manufacturer.name,
		sublabel: `${manufacturer.totalProducts} ${manufacturer.totalProducts === 1 ? 'produto atrelado' : 'produtos atrelados'}`,
		icon: (
			<div className={`${styles.iconWrapper} ${ICON_BG_CLASSES[index % ICON_BG_CLASSES.length]}`}>
				<FactoryOutlined
					className={`${styles.factoryIcon} ${ICON_CLASSES[index % ICON_CLASSES.length]}`}
				/>
			</div>
		),
	}));

	const popularProductSlot = isMostPopularProductError ? (
		<div role="alert">
			<EmptyState title="Não foi possível carregar o produto em destaque" />
		</div>
	) : isMostPopularProductLoading ? (
		<SkeletonBox height="11rem" borderRadius="0.75rem" />
	) : !mostPopularProduct ? (
		<EmptyState
			title="Nenhum produto em destaque ainda"
			description="Quando um produto for recomendado em alguma análise, ele aparece aqui automaticamente."
		/>
	) : mostPopularProduct.avatars.length ? (
		<ProductPhotoCarousel images={mostPopularProduct.avatars} />
	) : (
		<ProductModel
			type={mostPopularProduct.type === 'defensive' ? 'defensives' : 'fertilizers'}
			nature={mostPopularProduct.nature ?? ''}
			name={mostPopularProduct.name}
			manufacturer={mostPopularProduct.manufacturer}
		/>
	);

	const handleAddClick = onAddButtonClick ?? (() => onAddNew?.(activeTab));

	const handleImportFile = (file: File) => {
		if (onImport) {
			onImport(file, activeTab);
			return;
		}

		switch (activeTab) {
			case 'defensives':
				importDefensiveFile.mutate(
					{ file },
					{
						onSuccess: showImportResultToast,
					}
				);
				break;
			case 'fertilizers':
				importFertilizerFile.mutate(
					{ file },
					{
						onSuccess: showImportResultToast,
					}
				);
				break;
			case 'manufacturers':
				importManufacturerFile.mutate(
					{ file },
					{
						onSuccess: showImportResultToast,
					}
				);
				break;
		}
	};

	return (
		<>
			<ImportProductsModal
				isOpen={isImportModalOpen}
				onClose={() => setIsImportModalOpen(false)}
				onImport={handleImportFile}
			/>
			<ListPageLayout
				sidebarWidth="26rem"
				sidebarW1500="22rem"
				sidebarW1300="19rem"
				sidebarW1200="17rem"
				stackAt={1100}
				sidebar={
					<div className={styles.charts}>
						<div className={styles.content} aria-busy={isMostPopularProductFetching}>
							{isMostPopularProductFetching &&
								!isMostPopularProductLoading &&
								!isMostPopularProductError && (
									<span role="status" className={styles.refreshStatus}>
										Atualizando produto…
									</span>
								)}
							<BaseCard title="Produto Mais Popular" subtitle="O produto mais usados no Practium">
								{popularProductSlot}
							</BaseCard>
						</div>
						<div className={styles.content} aria-busy={isMostPopularManufacturersFetching}>
							{isMostPopularManufacturersFetching &&
								!isMostPopularManufacturersLoading &&
								!isMostPopularManufacturersError && (
									<span role="status" className={styles.refreshStatus}>
										Atualizando fabricantes…
									</span>
								)}
							{isMostPopularManufacturersError ? (
								<BaseCard
									title="Fabricantes Populares"
									subtitle="Os fabricantes mais usados no Practium"
									stretch
								>
									<div role="alert">
										<EmptyState title="Não foi possível carregar os fabricantes populares" />
									</div>
								</BaseCard>
							) : (
								<SideListCard
									title="Fabricantes Populares"
									subtitle="Os fabricantes mais usados no Practium"
									items={sideListItems}
									isLoading={isMostPopularManufacturersLoading}
									stretch={true}
								/>
							)}
						</div>
					</div>
				}
			>
				<SectionCard
					title="Catálogo de Produtos"
					subtitle="Gerencie defensivos, fertilizantes e fabricantes cadastrados no sistema"
					tabs={tabs.map((tab) => ({ ...tab, content: tableStatus ?? tab.content }))}
					isLoading={isTableLoading && !isTableError}
					isFetching={isTableFetching && !isTableError}
					loadingLabel="Carregando catálogo"
					activeTab={activeTab}
					onTabChange={setActiveTab}
					search={true}
					searchQuery={searchQuery}
					onSearch={setSearchQuery}
					sortAsc={sortAsc}
					onToggleSort={toggleSort}
					filterSlot={
						<div className={styles.filterContent} aria-busy={isFiltersLoading}>
							<FilterChip
								values={filterValues}
								onChange={setFilterValues}
								groups={filterGroups}
								placeholder="Filtros"
							/>
							{isFiltersLoading && (
								<span role="status" className={styles.filterStatus}>
									Carregando opções…
								</span>
							)}
							{isFiltersError && (
								<span role="alert" className={styles.filterStatus}>
									Não foi possível carregar algumas opções de filtro.
								</span>
							)}
						</div>
					}
					footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
					headerAction={
						<>
							{addButtonText && <ActionButton text={addButtonText} onClick={handleAddClick} />}
							{hookProps.permissions.canCreate && (
								<ActionButton text="Importar" onClick={() => setIsImportModalOpen(true)} />
							)}
							<ExportDropdown
								disabled={isTableLoading || isTableError || isExporting}
								label={isExporting ? 'Exportando…' : 'Exportar'}
								onExportExcel={() => {
									if (activeTab === 'defensives')
										void handleExportProducts('defensives', 'xlsx', {
											filter: remoteFilter,
											sortAsc: remoteSortAsc,
											class: parsedFilters.class,
											nature: parsedFilters.nature,
											origin: parsedFilters.origin,
											manufacturer: parsedFilters.manufacturer,
										});
									else if (activeTab === 'fertilizers')
										void handleExportProducts('fertilizers', 'xlsx', {
											filter: remoteFilter,
											sortAsc: remoteSortAsc,
											category: parsedFilters.category,
											nature: parsedFilters.nature,
											origin: parsedFilters.origin,
											manufacturer: parsedFilters.manufacturer,
										});
									else exportManufacturersToExcel(filteredManufacturers);
								}}
								onExportPdf={() => {
									if (activeTab === 'defensives')
										void handleExportProducts('defensives', 'pdf', {
											filter: remoteFilter,
											sortAsc: remoteSortAsc,
											class: parsedFilters.class,
											nature: parsedFilters.nature,
											origin: parsedFilters.origin,
											manufacturer: parsedFilters.manufacturer,
										});
									else if (activeTab === 'fertilizers')
										void handleExportProducts('fertilizers', 'pdf', {
											filter: remoteFilter,
											sortAsc: remoteSortAsc,
											category: parsedFilters.category,
											nature: parsedFilters.nature,
											origin: parsedFilters.origin,
											manufacturer: parsedFilters.manufacturer,
										});
									else exportManufacturersToPdf(filteredManufacturers);
								}}
							/>
						</>
					}
				/>
			</ListPageLayout>
		</>
	);
};
